// PersonProfileForm.tsx
import React, { useEffect, useState } from 'react'
import { useForm, Controller, FieldErrors, Resolver } from 'react-hook-form'
import { Input, Box, SxProps } from '@mui/material'
import { EdifyFieldLabel, EdifyFormField, EdifySelect } from '../../components/form'
import { phoneInputFormatter } from '../../../core/utils/input-formatters/InputFormatters'
import FormErrorText from '../../components/form/FormErrorText'

import { personUpdate, saveToLocalStorage } from '../../../data/data'
import { ErrorToast, SuccessToast } from '../../../core/utils/toast-notifications/ToastNotifications'
import { adminEditUser, getCurrentPerson } from '../../../domain/domain'
import { IVerifyOTPPayload } from '../../../domain/interfaces/IUserDetails'
import { EdifyButton } from '../../components/buttons'
import NotificationsSettings from '../../components/forms/PersonProfileForm/NotificationsSettings'
import InnerPageContainer from '../../components/inner-page-container/InnerPageContainer'
import { useOrganizationProvider } from '../../../providers/OrganizationProvider'
import EdifySearchDropdown, { ISearchResult } from '../../components/form/EdifySearch/EdifySearchDropdown'
import { useUserPageViewModel } from './UserPageViewModel'
import ErrorContainer from '../../components/error/ErrorContainer'
import EdifyMenuItem from '../../components/form/EdifyMenuItem'
import { formatSnakeCase } from '../../../core/utils/text-formaters/TextFormatters'
import { IRole } from '../../../domain/interfaces/IRole'
import UserFormNotificationsDataGrid from './UserFormNotificationDataGrid'
import { ROUTE_USERS } from '../users/UsersPage'
import { useNavigate } from 'react-router-dom'

type ProfileInputs = {
  fullName: string
  email: string
  phone: string
  role: string
}

interface IProfileFormProps {
  setUserName: (name: string) => void
  uploadImage?: string | null
  sx?: SxProps
  showName?: string
  canEdit: boolean
}

export const CurrentUserPersonForm: React.FC<IProfileFormProps> = ({
  setUserName,
  uploadImage,
  showName,
  canEdit,

  sx,
}) => {
  const { resetProfileTileCallback } = useOrganizationProvider()
  const [person, setPerson] = useState<IVerifyOTPPayload | undefined>(undefined)
  const [emailChecked, setEmailChecked] = useState<boolean>(true)
  const [smsChecked, setSMSChecked] = useState<boolean>(false)
  const [SMSTermsChecked, setSMSTermsChecked] = useState<boolean>(false)
  const [SMSTermsChanged, setSMSTermsChanged] = useState<boolean>(false)
  const [notificationError, setNotificationError] = useState<string | undefined>(undefined)
  const vm = useUserPageViewModel()
  const [projects, setProjects] = useState<ISearchResult[]>([])
  const [levels, setLevels] = useState<ISearchResult[]>([])
  const [pointOfContact, setPointOfContact] = useState<ISearchResult | undefined>(undefined)
  const [contractor, setContractor] = useState<ISearchResult | undefined>(undefined)
  const { getHierarchyName } = useOrganizationProvider()
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const projectName = getHierarchyName(0, true)
  const levelName = getHierarchyName(1, true)
  const navigate = useNavigate()

  const resolver: Resolver<ProfileInputs> = async values => {
    const errors: FieldErrors<ProfileInputs> = {}

    if (!values.fullName) {
      errors.fullName = {
        type: 'required',
        message: 'Name is required',
      }
    }
    // Check if at least one contact method is provided
    if (!values.email && !values.phone) {
      errors.email = {
        type: 'required',
        message: 'Either email or phone number is required',
      }
      errors.phone = {
        type: 'required',
        message: 'Either email or phone number is required',
      }
    }

    if (values.phone) {
      const phoneNumberPattern = /^\d{3}-\d{3}-\d{4}$/
      if (!phoneNumberPattern.test(values.phone)) {
        errors.phone = {
          type: 'pattern',
          message: 'Invalid phone number',
        }
      }
    }
    return {
      values,
      errors,
    }
  }

  const {
    register,
    handleSubmit,
    setValue,
    control,
    formState: { errors },
    clearErrors,
  } = useForm<ProfileInputs>({
    resolver,
    mode: 'onSubmit',
  })

  const handlePhoneInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target
    const formattedValue = phoneInputFormatter(value)
    if (value.length > 0) {
      clearErrors(['phone', 'email'])
    }
    setValue('phone', formattedValue, {
      shouldValidate: value.length > 11 || value.length === 0,
    })
  }

  const updatePerson = async (data: ProfileInputs) => {
    // const newPhone = data.phone.replaceAll('-', '')

    const payload = {
      // only add phone and email data if it was changed
      ...(data.phone.replaceAll('-', '') !== person?.phone?.replaceAll('-', '') && { phone: data.phone }),
      ...(data.email.toLocaleLowerCase() !== person?.email?.toLocaleLowerCase() && { email: data.email }),
      imageURL: uploadImage ?? '',
      fullName: data.fullName,
      smsNotifEnabled: smsChecked,
      emailNotifEnabled: emailChecked,
    }
    const res = await personUpdate(payload, person?.id ?? '')

    if (res.isLeft()) {
      const title = res.left?.message ?? 'Error saving new changes.'
      ErrorToast({ title: title })
      return false
    }
    // Only sho
    if (!canEdit) {
      SuccessToast({ title: 'Profile Successfully Update.' })
    }

    setUserName(data.fullName)

    //only modify currentPerson if modifying self
    const currentPerson = getCurrentPerson()
    if (currentPerson?.id === res.right?.id) {
      res.right && saveToLocalStorage('person', JSON.stringify(res.right))
      resetProfileTileCallback(data.fullName, uploadImage ?? '')
    }
    return true
  }

  const updateUser = async (data: ProfileInputs) => {
    const role = data.role
    const selectedProjects = projects.map(p => p.id)
    const selectedLevels = levels.map(l => l.id)
    const pointOfContactId = pointOfContact?.id
    const id = vm.user?.id
    const contractorId = contractor?.id
    const result = await adminEditUser(
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      id!,
      data.fullName ?? '',
      '', //emailOrPhone not in use anymore because it is modified in the person.
      [role],
      selectedProjects,
      selectedLevels,
      pointOfContactId,
      contractorId,
    )

    if (result.isLeft()) {
      ErrorToast({ title: 'Error saving new changes.' })
    } else {
      SuccessToast({ title: 'Profile Successfully Update.' })
      navigate(`${ROUTE_USERS}/${id}`)
    }
  }

  const onSubmit = handleSubmit(async data => {
    setIsLoading(true)
    const updatePersonSuccessful = await updatePerson(data)
    if (canEdit && updatePersonSuccessful) {
      await updateUser(data)
    }
    setIsLoading(false)
  })

  useEffect(() => {
    if (!emailChecked && !smsChecked) {
      setNotificationError('You must select at least one of these options')
    } else {
      setNotificationError(undefined)
    }
  }, [emailChecked, smsChecked])
  useEffect(() => {
    const personData = vm.person
    setPerson(personData ?? undefined)
    setEmailChecked(personData?.emailNotifEnabled ?? true)
    setSMSChecked(personData?.smsNotifEnabled ?? false)

    setValue('fullName', personData?.fullName ?? '')
    setValue('email', personData?.email ?? '')
    setValue('phone', personData?.phone ? phoneInputFormatter(personData?.phone) : '')
  }, [setValue, vm.person])

  const projectSelected = (projects: ISearchResult[]) => {
    setProjects(projects)
  }

  const levelSelected = (levels: ISearchResult[]) => {
    setLevels(levels)
  }

  const contactSelected = (contacts: ISearchResult[]) => {
    setPointOfContact(contacts[0])
  }
  const contractorSelected = (contractor: ISearchResult[]) => {
    setContractor(contractor[0])
  }

  const setInitPointOfContact = () => {
    if (vm.user && vm.user.pointOfContactId) {
      return [
        {
          id: vm.user?.pointOfContactId,
          name: vm.user?.pointOfContactName ?? '',
        },
      ]
    } else return []
  }

  const setInitContractor = () => {
    if (vm.user && vm.user.contractor) {
      return [
        {
          name: vm.user.contractor?.name ?? '',
          id: vm.user.contractor.id ?? '',
        },
      ]
    } else return []
  }

  useEffect(() => {
    setProjects(vm.user?.projects ?? [])
    setLevels(vm.user?.levels ?? [])
    setPointOfContact({
      id: vm.user?.pointOfContactId ?? '',
      name: vm.user?.pointOfContactName ?? '',
    })
    setContractor({
      name: vm.user?.contractor?.name ?? '',
      id: vm.user?.contractor?.id ?? '',
    })
  }, [vm.user])

  return (
    <InnerPageContainer headerTitle='Profile Settings' sx={{ ...sx }}>
      <form onSubmit={onSubmit}>
        <Box style={{ display: 'flex', flex: 1 }}>
          <EdifyFormField sx={{ flex: 1, marginRight: '24px' }}>
            <EdifyFieldLabel required label='NAME' />
            <Input
              disableUnderline
              placeholder='Enter name'
              {...register('fullName', { required: 'Name is required' })}
              className={`form-control ${errors?.fullName ? 'error' : ''}`}
            />
            {errors.fullName && <FormErrorText>{errors.fullName.message}</FormErrorText>}
          </EdifyFormField>
        </Box>
        <Box style={{ display: 'flex', flex: 1 }}>
          <EdifyFormField sx={{ flex: 1, marginRight: '24px' }}>
            <EdifyFieldLabel label='PHONE NUMBER' />
            <Controller
              name='phone'
              control={control}
              defaultValue=''
              render={({ field }) => (
                <Input
                  {...field}
                  data-testid='PhoneNumber'
                  disableUnderline
                  placeholder='000-000-0000'
                  className={`form-control ${errors?.phone ? 'error' : ''}`}
                  value={field.value}
                  onChange={handlePhoneInputChange}
                />
              )}
            />
            {errors?.phone && <FormErrorText>{errors.phone.message}</FormErrorText>}
          </EdifyFormField>
          <EdifyFormField sx={{ flex: 1, marginRight: '24px' }}>
            <EdifyFieldLabel label='EMAIL' />
            <Input
              data-testid='Email'
              disableUnderline
              placeholder='sample@email.com'
              className={`form-control ${errors?.email ? 'error' : ''}`}
              {...register('email', {
                onChange: () => clearErrors(['email', 'phone']),
              })}
            />
            {errors?.email && <FormErrorText>{errors.email.message}</FormErrorText>}
          </EdifyFormField>
        </Box>

        <NotificationsSettings
          setEmailChecked={setEmailChecked}
          setSMSChecked={setSMSChecked}
          emailChecked={emailChecked}
          smsChecked={smsChecked}
          setSMSTermsChecked={setSMSTermsChecked}
          SMSTermsChecked={SMSTermsChecked}
          setSMSTermsChanged={setSMSTermsChanged}
          SMSTermsChanged={SMSTermsChanged}
          notificationError={notificationError}
        />
        {notificationError && <FormErrorText>{notificationError}</FormErrorText>}

        {/* USER SECTION */}
        {canEdit && (
          <InnerPageContainer headerTitle='User Settings' sx={{ ...sx, padding: 0, mt: 36 }}>
            {vm.formError && <ErrorContainer>{vm.formError}</ErrorContainer>}

            {showName && (
              <Box style={{ display: 'flex', flex: 1 }}>
                <EdifyFormField sx={{ flex: 1, marginRight: '24px' }}>
                  <EdifyFieldLabel disabled label='NAME' />
                  <Input disableUnderline disabled defaultValue={vm.user?.fullName} className='form-control' />
                </EdifyFormField>
              </Box>
            )}
            <Box>
              <EdifyFormField>
                <EdifyFieldLabel required label='SELECT ROLE' />
                <Controller
                  name='role'
                  control={control}
                  defaultValue={vm.user?.roles[0]?.id ?? undefined}
                  rules={{
                    required: 'Role is required',
                  }}
                  render={({ field }) => (
                    <EdifySelect
                      width={483}
                      value={field.value}
                      onChange={field.onChange}
                      onBlur={field.onBlur}
                      dropDownItems={vm.roles ?? []}
                      renderMenuItem={(item: IRole) => (
                        <EdifyMenuItem key={item.id} value={item.id}>
                          {formatSnakeCase(item.name)}
                        </EdifyMenuItem>
                      )}
                    />
                  )}
                />
                {errors.role && <FormErrorText>{errors.role.message}</FormErrorText>}
              </EdifyFormField>
              <EdifyFormField>
                <EdifyFieldLabel label='Contractor' />
                <EdifySearchDropdown
                  pillListWidth={'100%'}
                  key={contractor?.id || 'no-contractor'}
                  initialSelectedItems={contractor ? [contractor] : []}
                  width={483}
                  onSelect={contractorSelected}
                  nameKey='name'
                  limit={16}
                  globalSearchKey='contractors'
                />
              </EdifyFormField>

              <EdifyFormField>
                <EdifyFieldLabel label='Point Of Contact' />
                <EdifySearchDropdown
                  pillListWidth={'100%'}
                  key={pointOfContact?.id || 'no-point-of-contact'}
                  initialSelectedItems={pointOfContact ? [pointOfContact] : []}
                  width={483}
                  onSelect={contactSelected}
                  nameKey='fullName'
                  limit={16}
                  globalSearchKey='users'
                />
              </EdifyFormField>

              <EdifyFormField>
                <EdifyFieldLabel label={`SELECT ${levelName.toUpperCase()}`} />
                <EdifySearchDropdown
                  multiSelect
                  pillListWidth={'100%'}
                  width={484}
                  onSelect={levelSelected}
                  initialSelectedItems={vm.user?.levels}
                  globalSearchKey='levels'
                />
              </EdifyFormField>
              <EdifyFormField>
                <EdifyFieldLabel label={`SELECT ${projectName.toUpperCase()}`} />
                <EdifySearchDropdown
                  multiSelect
                  pillListWidth={'100%'}
                  width={484}
                  onSelect={projectSelected}
                  initialSelectedItems={vm.user?.projects}
                  globalSearchKey='projects'
                />
              </EdifyFormField>
            </Box>

            <Box sx={{ mt: '24px' }}>
              <hr />
              <InnerPageContainer
                collapsible
                collapsed={true}
                headerTitle='Form Subscriptions Settings'
                sx={{ mt: '24px' }}
              >
                <Box sx={{ mb: '24px' }} />

                <UserFormNotificationsDataGrid
                  title='Form Subscriptions'
                  readonly={false}
                  checkboxSelection={false}
                  showAll={true}
                />
              </InnerPageContainer>
            </Box>
          </InnerPageContainer>
        )}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'flex-end',
            marginTop: '20px',
          }}
        >
          <EdifyButton disabled={isLoading} type='submit' primary title='Save Changes' />
        </Box>
      </form>
    </InnerPageContainer>
  )
}

export default CurrentUserPersonForm
